import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CreditCard, Loader2, RefreshCw, LogOut, ShieldAlert, Mail, CheckCircle2 } from "lucide-react";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useAuth } from "@/hooks/useAuth";
import { getPricingContext, getPricingTexts } from "@/lib/pricingContext";

/**
 * Page affichée quand l'accès est bloqué (status = 'past_due', 'canceled' avec Stripe, 'unpaid')
 * Design aligné sur la landing page b8ild.fr
 */
export default function PaymentBlocked() {
  const navigate = useNavigate();
  const { user, loading: authLoading, signOut } = useAuth();
  const {
    status,
    hasStripeSubscription,
    isBlocked,
    loading: subscriptionLoading,
    openCustomerPortal,
    refreshSubscription,
  } = useSubscription();

  // Rediriger vers auth si pas connecté
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
    }
  }, [user, authLoading, navigate]);

  // Si l'utilisateur n'est plus bloqué, rediriger vers le dashboard
  useEffect(() => {
    if (!subscriptionLoading && !isBlocked) {
      navigate('/dashboard');
    }
  }, [isBlocked, subscriptionLoading, navigate]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleRefresh = async () => {
    await refreshSubscription();
  };

  const handleOpenPortal = async () => {
    await openCustomerPortal();
  };

  // Déterminer le contexte pricing et les textes adaptés (hooks avant les early returns)
  const pricingContext = useMemo(() => getPricingContext({
    status,
    hasStripeSubscription,
    hasHadTrial: false,
    isInTrial: false,
  }), [status, hasStripeSubscription]);

  const texts = useMemo(() => getPricingTexts(pricingContext), [pricingContext]);

  // canceled_paid → "Se réabonner" redirige vers payment-setup pour choisir un plan
  const isCanceledPaid = pricingContext === 'canceled_paid';
  // unpaid → "Nous contacter" en bouton secondaire
  const isUnpaidContext = pricingContext === 'unpaid';

  if (subscriptionLoading || authLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#F59E0B]" />
      </div>
    );
  }

  const handlePrimaryAction = () => {
    if (isCanceledPaid) {
      // Rediriger vers la page de choix de plan
      navigate('/payment-setup?choose=true');
    } else {
      // past_due / unpaid → ouvrir le portail Stripe
      handleOpenPortal();
    }
  };

  const handleSecondaryAction = () => {
    if (isUnpaidContext) {
      // "Nous contacter"
      window.location.href = 'mailto:contact@b8ild.fr';
    } else {
      // "Vérifier mon statut" ou "Changer de plan"
      handleRefresh();
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#0A0A0F]">
      {/* Gradient mesh background — identique LP */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0F] via-[#0F172A] to-[#0A0A0F]" />
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#F59E0B]/8 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#1A365D]/30 rounded-full blur-[120px]" />
      </div>

      {/* Grille subtile — identique LP */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:64px_64px]" />

      {/* Header — style LP flottant arrondi */}
      <header className="fixed top-0 left-0 right-0 z-50 w-full">
        <div className="relative mx-auto px-4 sm:px-6">
          <div className="relative flex h-16 items-center justify-between border-b border-white/[0.06] bg-[#0A0A0F]/80 backdrop-blur-xl rounded-[20px] mx-4 mt-2 px-6">
            {/* Logo texte seul — identique LP */}
            <span className="text-2xl font-bold text-white tracking-tight">
              B<span className="text-[#F59E0B]">8</span>ild
            </span>

            {/* Déconnexion */}
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Se déconnecter
            </button>
          </div>
        </div>
      </header>

      {/* Contenu centré */}
      <div className="relative z-10 container mx-auto px-4 pt-28 pb-16 lg:pt-36 lg:pb-24 max-w-xl">
        <div className="text-center space-y-6">
          {/* Icône */}
          <div className="mx-auto w-20 h-20 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center">
            <ShieldAlert className="h-10 w-10 text-[#F59E0B]" />
          </div>

          {/* Titre — contextuel */}
          <h1 className="text-3xl lg:text-4xl font-bold text-white tracking-tight">
            {texts.title}
          </h1>

          {/* Description — contextuelle */}
          <p className="text-lg text-white/50 max-w-md mx-auto">
            {texts.subtitle}
          </p>

          {/* Mention rassurante */}
          {texts.reassurance && (
            <p className="text-base text-green-400 font-medium flex items-center justify-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              {texts.reassurance}
            </p>
          )}
        </div>

        {/* Card principale — style LP */}
        <div className="mt-10 rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.1] to-transparent">
          <div className="rounded-2xl bg-white/[0.03] backdrop-blur-xl p-6 space-y-5">
            {/* Détails du problème — bullets contextuels */}
            {texts.bullets && texts.bullets.length > 0 && (
              <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-4 space-y-3">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-[#F59E0B]" />
                  Que se passe-t-il ?
                </h3>
                <ul className="text-sm text-white/50 space-y-2">
                  {texts.bullets.map((bullet, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-[#F59E0B] mt-1.5 shrink-0" />
                      {bullet}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Boutons d'action */}
            <div className="space-y-3">
              {/* CTA principal — style LP exact */}
              <Button
                size="lg"
                className="w-full h-12 gap-2 font-semibold text-base rounded-xl bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F] shadow-[0_0_0_1px_rgba(245,158,11,0.3),0_8px_40px_rgba(245,158,11,0.4)] hover:shadow-[0_0_0_1px_rgba(245,158,11,0.5),0_12px_50px_rgba(245,158,11,0.5)] hover:scale-[1.02] transition-all duration-300"
                onClick={handlePrimaryAction}
              >
                <CreditCard className="h-5 w-5" />
                {texts.buttonLabel}
              </Button>

              {/* Bouton secondaire — style LP outline */}
              {texts.secondaryButtonLabel && (
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full h-12 gap-2 rounded-xl border-white/[0.08] bg-white/[0.03] text-white hover:bg-white/[0.06] backdrop-blur-sm transition-all duration-200"
                  onClick={handleSecondaryAction}
                >
                  {isUnpaidContext ? (
                    <Mail className="h-4 w-4" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {texts.secondaryButtonLabel}
                </Button>
              )}
            </div>

            {/* Contact support */}
            <div className="text-center pt-3 border-t border-white/[0.06]">
              <p className="text-sm text-white/40">
                Besoin d'aide ? Contactez-nous à{" "}
                <a
                  href="mailto:contact@b8ild.fr"
                  className="text-[#F59E0B] hover:text-[#FBBF24] hover:underline font-medium transition-colors"
                >
                  contact@b8ild.fr
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
